'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { rewriteCatalogCdnUrl } from '../../../../lib/config/cdn';
import { staticUrl } from '../../../../lib/config/static-assets';
import './character-card-modal.css';

type Entry = Record<string, unknown>;
type Kind = 'member' | 'support';
type CardItem = { sourceId: string; image: string; fullImage: string; raw: Entry; catalogId?: string };
type CardRenderer = ((args: { item: Entry; escapeHtml: (value: unknown) => string; displayCdnUrl: (value: string) => string }) => string) & { mount?: (backdrop: HTMLElement, item: Entry) => void };

declare global {
  interface Window {
    GameWikiCardInteractions?: (options: { genericMedia: HTMLElement; filteredSiblings: CardItem[]; activeSiblingIndex: number; renderContent: { useCardSwipeEffect: boolean; isBangdreamCard: boolean }; onSwitch: (index: number) => void }) => () => void;
    GameWikiIndependentModalRenderers?: Record<string, CardRenderer>;
    BangDreamMemberAnimation?: { has: (cardId: unknown) => boolean; mount: (container: Element | null, cardId: number, loopOnly?: boolean) => HTMLVideoElement | null };
  }
}

const catalogKeys = {
  member: 'catalog_675c26b105284ffe87eb36f6471f8dfc',
  support: 'catalog_7d76498e4b094e88bbe587f2a3d465ab',
};
const plain = (value: unknown) => typeof value === 'string' || typeof value === 'number' ? String(value) : '';
const escapeHtml = (value: unknown) => plain(value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] || character);

function artUrl(card: Entry, kind: Kind, base: string, full: boolean) {
  const direct = plain(card[full ? 'fullImage' : 'image']);
  if (direct) return rewriteCatalogCdnUrl(direct);
  const art = card.art && typeof card.art === 'object' ? card.art as Entry : {};
  const key = kind === 'member' ? (full ? 'member_full' : 'member_thumbnail') : (full ? 'snap_full' : 'snap_thumbnail');
  const source = art[key];
  const path = source && typeof source === 'object' ? plain((source as Entry).site_path) : plain(source);
  if (!path) return '';
  try { return rewriteCatalogCdnUrl(new URL(path.startsWith('//') ? `https:${path}` : path, base).toString()); }
  catch { return rewriteCatalogCdnUrl(path); }
}

function loadAsset(tag: 'link' | 'script', url: string) {
  url = staticUrl(url);
  const existing = document.querySelector<HTMLElement>(`[data-character-card-asset="${url}"]`);
  if (existing?.dataset.loaded === 'true') return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const element = existing || document.createElement(tag);
    element.addEventListener('load', () => { element.dataset.loaded = 'true'; resolve(); }, { once: true });
    element.addEventListener('error', () => reject(new Error('卡牌弹窗资源加载失败')), { once: true });
    if (!existing) {
      element.dataset.characterCardAsset = url;
      if (tag === 'link') { const link = element as HTMLLinkElement; link.rel = 'stylesheet'; link.href = url; document.head.append(link); }
      else { (element as HTMLScriptElement).src = url; document.head.append(element); }
    }
  });
}

function fallbackDetail(item: CardItem, kind: Kind, characterName: string) {
  const card = item.raw;
  return {
    name: plain(card.title) || plain(card.name), type: kind === 'member' ? '成员卡' : '支援卡',
    grade: kind === 'member' ? plain(card.rarity) : `稀有度 ${plain(card.rarity_code)}`,
    image: item.image, fullImage: item.fullImage || item.image,
    characterName: kind === 'member' ? [characterName, plain(card.display_name)].filter(Boolean).join(' / ') : plain(card.name),
    level1: card.level_1, maxBuild: card.max_build, skills: card.skills, growth: card.growth,
  } as Entry;
}

export default function CharacterCardModal({ game, base, characterName, memberCards, supportCards, onRegister }: {
  game: string; base: string; characterName: string; memberCards: Entry[]; supportCards: Entry[];
  onRegister: (open: (kind: Kind, index: number) => void) => void;
}) {
  const [kind, setKind] = useState<Kind | null>(null);
  const [items, setItems] = useState<CardItem[]>([]);
  const itemsRef = useRef<CardItem[]>([]);
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState<Entry | null>(null);
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState('');
  const [viewerScale, setViewerScale] = useState(1);
  const [viewerOffset, setViewerOffset] = useState({ x: 0, y: 0 });
  const viewerDrag = useRef<{ id: number; x: number; y: number; originX: number; originY: number } | null>(null);
  const suppressSwipeClick = useRef(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  const switchCardRef = useRef<(targetIndex: number) => void>(() => {});

  const preparedCards = useRef(new WeakMap<CardItem, Entry>());
  const pendingCards = useRef(new WeakMap<CardItem, Promise<Entry>>());
  const prepareCard = useCallback((item: CardItem, nextKind: Kind): Promise<Entry> => {
    const pending = pendingCards.current.get(item);
    if (pending) return pending;
    const request = (async () => {
      const detailUrl = item.catalogId
        ? `/api/games/${encodeURIComponent(game)}/catalogs/${catalogKeys[nextKind]}/generic/items?id=${encodeURIComponent(item.catalogId)}`
        : staticUrl(`/catalog-independent/bangdream/sakiko/details/${encodeURIComponent(item.sourceId)}.json`);
      const result = await fetch(detailUrl).then(response => {
        if (!response.ok) throw new Error('卡牌详情读取失败');
        return response.json() as Promise<Entry>;
      });
      const nextDetail = { ...fallbackDetail(item, nextKind, characterName), ...result };
      const image = new Image();
      image.src = rewriteCatalogCdnUrl(plain(nextDetail.fullImage) || plain(nextDetail.image));
      await image.decode();
      preparedCards.current.set(item, nextDetail);
      return nextDetail;
    })();
    pendingCards.current.set(item, request);
    void request.catch(() => pendingCards.current.delete(item));
    return request;
  }, [game, characterName]);

  const selectCard = useCallback(async (nextItems: CardItem[], nextIndex: number, nextKind: Kind) => {
    const item = nextItems[nextIndex];
    if (!item) return;
    const currentRequest = ++requestId.current;
    setError('');
    try {
      const nextDetail = preparedCards.current.get(item) || await prepareCard(item, nextKind);
      if (currentRequest === requestId.current) { setIndex(nextIndex); setDetail(nextDetail); }
    } catch {
      if (currentRequest === requestId.current) { setIndex(nextIndex); setDetail(fallbackDetail(item, nextKind, characterName)); }
    }
  }, [prepareCard, characterName]);

  const open = useCallback(async (nextKind: Kind, nextIndex: number) => {
    const cards = nextKind === 'member' ? memberCards : supportCards;
    const nextItems: CardItem[] = cards.map(card => ({
      sourceId: plain(card.sourceId) || `${nextKind}-${plain(card.id)}`, image: artUrl(card, nextKind, base, false),
      fullImage: artUrl(card, nextKind, base, true), raw: card, catalogId: plain(card.catalogId) || undefined,
    }));
    setKind(nextKind);
    setDetail(null);
    setError('');
    setItems(nextItems);
    itemsRef.current = nextItems;
    setIndex(nextIndex);
    try {
      await loadAsset('script', '/legacy-game-wiki/card-interactions.js?v=20260930-shared-v1');
      await loadAsset('link', '/legacy-game-wiki/catalog-premium.css?v=20260930-landscape-support-v14');
      const cardStyleUrl = staticUrl(`/catalog-independent/bangdream/${catalogKeys[nextKind]}.css?v=20260930-independent-modal-v32`);
      await loadAsset('link', cardStyleUrl);
      const cardStyle = document.querySelector<HTMLLinkElement>(`link[data-character-card-asset="${cardStyleUrl}"]`);
      if (cardStyle) cardStyle.dataset[nextKind === 'member' ? 'bangdreamCardDetail' : 'bangdreamSupportCardDetail'] = '';
      await loadAsset('script', `/catalog-independent/bangdream/${catalogKeys[nextKind]}.js?v=20260930-independent-modal-v32`);
      try {
        const response = await fetch(`/api/games/${encodeURIComponent(game)}/catalogs/${catalogKeys[nextKind]}/generic/items`);
        if (response.ok) {
          const payload = await response.json() as { items?: Entry[] };
          const bySource = new Map((payload.items || []).map(item => [plain(item.sourceId), item]));
          nextItems.forEach(item => { const summary = bySource.get(item.sourceId); if (summary) item.catalogId = plain(summary.id); });
        }
      } catch { /* The character page already has enough card data for a fallback detail. */ }
      setItems([...nextItems]);
      await selectCard(nextItems, nextIndex, nextKind);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '卡牌弹窗加载失败');
    }
  }, [base, game, memberCards, supportCards, selectCard]);

  useEffect(() => { onRegister(open); }, [onRegister, open]);
  useEffect(() => {
    if (!kind) return;
    document.documentElement.classList.add('catalog-detail-open');
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { if (viewer) setViewer(''); else setKind(null); }
      if (!viewer && event.key === 'ArrowLeft') switchCardRef.current(index - 1);
      if (!viewer && event.key === 'ArrowRight') switchCardRef.current(index + 1);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.documentElement.classList.remove('catalog-detail-open'); document.removeEventListener('keydown', onKeyDown); };
  }, [kind, viewer, index, selectCard]);

  const renderer = kind && typeof window !== 'undefined' ? window.GameWikiIndependentModalRenderers?.[`bangdream/${catalogKeys[kind]}`] : undefined;
  const markup = detail && renderer ? renderer({ item: detail, escapeHtml, displayCdnUrl: rewriteCatalogCdnUrl }) : '';
  // Keep script-mounted carousel and video nodes intact during viewer updates.
  const cardContent = useMemo(() => markup
    ? <div className="catalog-detail-content" dangerouslySetInnerHTML={{ __html: markup }} />
    : <div className="catalog-detail-content"><p className="character-card-modal-loading">{error || '正在加载卡牌详情…'}</p></div>, [markup, error]);

  useEffect(() => {
    if (!viewer || kind !== 'member' || !window.BangDreamMemberAnimation?.has(detail?.cardId)) return;
    const modalPlayer = backdropRef.current?.querySelector<HTMLVideoElement>('.bd-card-media .bd-member-animation-player');
    modalPlayer?.pause();
    const viewport = document.querySelector('.character-card-image-viewport');
    const viewerPlayer = window.BangDreamMemberAnimation.mount(viewport, Number(detail?.cardId), false);
    return () => {
      viewport?.querySelectorAll<HTMLVideoElement>('.bd-member-animation-player').forEach(player => { player.pause(); player.remove(); });
      viewerPlayer?.pause();
      viewport?.querySelector('.bd-member-animation-background')?.remove();
      backdropRef.current?.querySelector<HTMLVideoElement>('.bd-card-media .bd-member-animation-player')?.play().catch(() => {});
    };
  }, [viewer, kind, detail]);
  useEffect(() => {
    const viewport = document.querySelector('.character-card-image-viewport');
    const animation = viewport?.querySelector<HTMLVideoElement>('.bd-member-animation-player');
    const image = viewport?.querySelector<HTMLImageElement>('img:not(.bd-member-animation-background)');
    if (!animation || !image) return;
    const update = () => {
      viewport?.querySelectorAll<HTMLElement>('.bd-member-animation-player,.bd-member-animation-background').forEach(layer => {
        layer.style.width = `${image.offsetWidth}px`;
        layer.style.height = `${image.offsetHeight}px`;
        layer.style.left = '50%';
        layer.style.top = '50%';
        layer.style.transform = `translate(-50%, -50%) translate(${viewerOffset.x}px, ${viewerOffset.y}px) scale(${viewerScale})`;
      });
    };
    update();
    image.addEventListener('load', update);
    return () => image.removeEventListener('load', update);
  }, [viewer, viewerScale, viewerOffset, detail]);

  useLayoutEffect(() => {
    const backdrop = backdropRef.current;
    if (!backdrop || !detail || !kind || !renderer) return;
    renderer.mount?.(backdrop, detail);
    const media = backdrop.querySelector<HTMLElement>('.bd-card-media');
    if (!media) return;
    const card = (step: number) => {
      const target = items[index + step];
      if (!target) return '';
      const position = step === -2 ? 'before-previous' : step === -1 ? 'previous' : step === 1 ? 'next' : 'after-next';
      return `<button type="button" class="bd-card-mobile-neighbor is-${position}" data-character-card-index="${index + step}" aria-label="${step < 0 ? '上一张' : '下一张'}：${escapeHtml(plain(target.raw.title))}"><img src="${escapeHtml(target.fullImage || target.image)}" alt="" draggable="false"></button>`;
    };
    if (kind === 'member') {
      const carousel = media.querySelector('.bd-card-carousel');
      carousel?.insertAdjacentHTML('afterbegin', card(-2) + card(-1));
      carousel?.insertAdjacentHTML('beforeend', card(1) + card(2) + [-1, 1].map(step => items[index + step] ? `<button type="button" class="bd-card-carousel-arrow ${step < 0 ? 'is-previous' : 'is-next'}" data-character-card-index="${index + step}" aria-label="${step < 0 ? '上一张' : '下一张'}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="${step < 0 ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>` : '').join(''));
      carousel?.insertAdjacentHTML('afterend', `<div class="bd-card-mobile-count">${index + 1}/${items.length}</div>`);
    } else {
      const stage = media.querySelector('.bd-support-image-stage');
      stage?.insertAdjacentHTML('beforeend', [-1, 1].map(step => `<button type="button" class="bd-card-mobile-arrow ${step < 0 ? 'is-previous' : 'is-next'}" ${items[index + step] ? `data-character-card-index="${index + step}"` : 'disabled'} aria-label="${step < 0 ? '上一张' : '下一张'}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="${step < 0 ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`).join(''));
      stage?.insertAdjacentHTML('afterend', `<div class="bd-card-mobile-count">${index + 1}/${items.length}</div>`);
    }
    // Match the catalog entry point: prepare adjacent details and their full artwork.
    media.querySelectorAll<HTMLButtonElement>('.bd-card-mobile-neighbor').forEach(button => {
      const target = items[Number(button.dataset.characterCardIndex)];
      if (!target) return;
      void prepareCard(target, kind).then(prepared => {
        const image = button.querySelector('img');
        if (image && button.isConnected) image.src = rewriteCatalogCdnUrl(plain(prepared.fullImage) || plain(prepared.image));
      }).catch(() => {});
    });
    const surface = media.querySelector<HTMLElement>('.catalog-detail-card-surface');
    const background = surface?.querySelector<HTMLImageElement>('.bd-member-animation-background');
    const revealAnimation = () => {
      const playing = Array.from(surface?.querySelectorAll<HTMLVideoElement>('video') || []).some(video => video.readyState >= 2 && !video.paused);
      if (playing && background?.complete && background.naturalWidth) surface?.classList.add('is-animation-ready');
    };
    background?.addEventListener('load', revealAnimation);
    surface?.addEventListener('playing', revealAnimation, true);
    revealAnimation();
    media.querySelectorAll<HTMLElement>('[data-character-card-index]').forEach(button => {
      button.dataset.genericNeighbor = button.dataset.characterCardIndex;
    });
    switchCardRef.current = targetIndex => { void selectCard(itemsRef.current, targetIndex, kind); };
    const disposeInteractions = window.GameWikiCardInteractions?.({
      genericMedia: media,
      filteredSiblings: items,
      activeSiblingIndex: index,
      renderContent: { useCardSwipeEffect: kind === 'member', isBangdreamCard: true },
      onSwitch: targetIndex => { void selectCard(itemsRef.current, targetIndex, kind); },
    });
    return () => {
      disposeInteractions?.();
      background?.removeEventListener('load', revealAnimation);
      surface?.removeEventListener('playing', revealAnimation, true);
      media.querySelectorAll('video').forEach(player => player.pause());
    };
  }, [detail, kind, index, items, renderer, selectCard, prepareCard]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !kind) return;
    const active = list.querySelector<HTMLElement>('.is-active');
    if (!active) return;
    const listRect = list.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    const target = list.scrollLeft + activeRect.left - listRect.left - (listRect.width - activeRect.width) / 2;
    list.classList.add('is-positioning');
    list.scrollLeft = Math.max(0, target);
    requestAnimationFrame(() => list.classList.remove('is-positioning'));
  }, [kind, index, items]);

  if (!kind || typeof document === 'undefined') return null;
  const listCards = items.map((item, itemIndex) => <button key={item.sourceId} type="button" className={itemIndex === index ? 'is-active' : ''} aria-current={itemIndex === index ? 'true' : undefined} aria-label={`切换到${plain(item.raw.title)}`} onClick={() => void selectCard(itemsRef.current, itemIndex, kind)}><img src={item.image || item.fullImage} alt="" draggable={false} /></button>);
  const onContentClick = (event: MouseEvent<HTMLElement>) => {
    if (suppressSwipeClick.current) { event.preventDefault(); event.stopPropagation(); suppressSwipeClick.current = false; return; }
    const target = event.target as HTMLElement;
    const neighbor = target.closest<HTMLElement>('[data-character-card-index]');
    if (neighbor) return; // Shared gesture controller handles card navigation.
    if (target.closest('.bd-card-open-image')) { const url = plain(detail?.fullImage) || plain(detail?.image); if (url) { setViewer(url); setViewerScale(1); setViewerOffset({ x: 0, y: 0 }); } }
  };
  return createPortal(<>
    <div ref={backdropRef} className="catalog-detail-backdrop is-generic-detail is-bangdream-card-detail is-character-card-modal" data-catalog-key={catalogKeys[kind]} onMouseDown={(event) => { if (event.target === event.currentTarget) setKind(null); }}>
      <div className="catalog-detail-stage"><div className="catalog-detail-body">
        <section className="catalog-detail-dialog" role="dialog" aria-modal="true" aria-labelledby="catalog-detail-title" onClick={onContentClick}>
          <div className="character-card-close-anchor">
            <button type="button" className="catalog-detail-close" aria-label="关闭详情" onClick={() => setKind(null)}>×</button>
          </div>
          {cardContent}
          <div className="catalog-detail-desktop-count">{index + 1}/{items.length}</div>
        </section>
        {items.length > 1 && <nav className="catalog-detail-switcher" aria-label="当前角色的卡牌">
          <button type="button" className="catalog-detail-switch-arrow is-previous" disabled={index === 0} aria-label="上一张卡牌" onClick={() => void selectCard(itemsRef.current, index - 1, kind)}>‹</button>
          <div ref={listRef} className="catalog-detail-switch-list" onPointerDown={(event) => { if (event.button !== 0) return; const list = event.currentTarget; (list as HTMLElement & { dragData?: { x: number; scroll: number; moved: boolean } }).dragData = { x: event.clientX, scroll: list.scrollLeft, moved: false }; }} onPointerMove={(event) => { const list = event.currentTarget as HTMLElement & { dragData?: { x: number; scroll: number; moved: boolean } }; const drag = list.dragData; if (!drag) return; const delta = event.clientX - drag.x; if (Math.abs(delta) > 4) { drag.moved = true; list.classList.add('is-dragging'); list.setPointerCapture(event.pointerId); list.scrollLeft = drag.scroll - delta; event.preventDefault(); } }} onPointerUp={(event) => { const list = event.currentTarget as HTMLElement & { dragData?: { moved: boolean } }; if (list.dragData?.moved) list.dataset.suppressClick = 'true'; delete list.dragData; list.classList.remove('is-dragging'); if (list.hasPointerCapture(event.pointerId)) list.releasePointerCapture(event.pointerId); }} onClickCapture={(event) => { const list = event.currentTarget; if (list.dataset.suppressClick === 'true') { delete list.dataset.suppressClick; event.preventDefault(); event.stopPropagation(); } }} onDragStart={(event) => event.preventDefault()}>{listCards}</div>
          <button type="button" className="catalog-detail-switch-arrow is-next" disabled={index === items.length - 1} aria-label="下一张卡牌" onClick={() => void selectCard(itemsRef.current, index + 1, kind)}>›</button>
        </nav>}
      </div></div>
    </div>
    {viewer && <div className="character-card-image-viewer" role="dialog" aria-modal="true" aria-label="卡牌图片全屏预览" onMouseDown={(event) => { if (event.target === event.currentTarget) setViewer(''); }}>
      <div className="character-card-image-viewport" onWheel={(event) => { event.preventDefault(); setViewerScale(value => Math.max(1, Math.min(5, value * Math.exp(-event.deltaY * .002)))); }} onDoubleClick={() => { setViewerScale(value => value > 1 ? 1 : 2); setViewerOffset({ x: 0, y: 0 }); }} onPointerDown={(event) => { if (event.button !== 0) return; event.currentTarget.setPointerCapture(event.pointerId); viewerDrag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, originX: viewerOffset.x, originY: viewerOffset.y }; }} onPointerMove={(event) => { const drag = viewerDrag.current; if (!drag || drag.id !== event.pointerId || viewerScale <= 1) return; setViewerOffset({ x: drag.originX + event.clientX - drag.x, y: drag.originY + event.clientY - drag.y }); }} onPointerUp={() => { viewerDrag.current = null; }}>
        <img src={viewer} alt={plain(detail?.name)} draggable={false} style={{ transform: `translate(${viewerOffset.x}px,${viewerOffset.y}px) scale(${viewerScale})` }} />
      </div>
      <a className="character-card-image-download" href={viewer} download aria-label="下载图片" title="下载图片"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12m-4-4 4 4 4-4M4 17v3h16v-3" /></svg></a>
      <button type="button" className="character-card-image-close" aria-label="关闭图片预览" onClick={() => setViewer('')}><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
    </div>}
  </>, document.body);
}
